/// <amd-module name="Feed/Legacy/Protocols/NuGet/NuGetConnectToFeedPanel" />
import * as React from "react";

import { Dropdown, IDropdownOption } from "OfficeFabric/Dropdown";

import * as Utils_String from "VSS/Core/Util/String";
import { FormatComponent } from "VSS/Platform/Components/Format";
import { IVssPageService } from "VSS/Platform/Page";

import { CiConstants, FwLinks } from "Feed/Common/Constants/Constants";
import { FeedView } from "Feed/Common/Contracts/Feed";
import * as PackageResources from "Feed/Common/Resources";
import { ExternalLink } from "Feed/Legacy/Components/ExternalLink";
import { LabelWithExternalLink } from "Feed/Legacy/Components/LabelWithExternalLink";
import { PackageMessagePanel } from "Feed/Legacy/Components/PackageMessagePanel";
import { CustomerIntelligenceHelper } from "Feed/Legacy/Helpers/CustomerIntelligenceHelper";
import { isUserAnonymousOrPublic } from "Feed/Legacy/Helpers/PermissionHelper";
import { IConnectToFeedProps } from "Feed/Legacy/Protocols/Common/IConnectToFeedProps";
import { CopyableTextField, ICopyableTextFieldTelemetry } from "Feed/Legacy/Protocols/Components/CopyableTextField";
import { NuGetCiConstants } from "Feed/Legacy/Protocols/NuGet/Constants/NuGetConstants";
import { HubAction } from "Feed/Legacy/Types/IHubState";
import { INuGetRestApiUrlGeneratorService } from "Protocols/NuGet/NuGetRestApiUrlGeneratorService";

export interface INuGetConnectToFeedPanelProps extends IConnectToFeedProps {
    feedViews: FeedView[];
}

export interface INuGetConnectToFeedPanelState {
    endpointUrl: string;
    selectedView: FeedView;
}

export class NuGetConnectToFeedPanel extends React.Component<
    INuGetConnectToFeedPanelProps,
    INuGetConnectToFeedPanelState
> {
    private showProjectInUrl: boolean;

    constructor(props: INuGetConnectToFeedPanelProps) {
        super(props);

        const selectedView = props.feed && props.feed.view ? props.feed.view : null;
        this.showProjectInUrl = props.feed && props.feed.project != null;
        this.state = {
            endpointUrl: props.pageContext
                .getService<INuGetRestApiUrlGeneratorService>("INuGetRestApiUrlGeneratorService")
                .getServiceIndexUrl(this._getFullyQualifiedFeedName(selectedView), this.showProjectInUrl),
            selectedView
        } as INuGetConnectToFeedPanelState;

        CustomerIntelligenceHelper.publishEvent(this.props.pageContext, CiConstants.ConnectToFeed, {
            Action: CiConstants.ConnectToFeedTabChanged,
            Protocol: NuGetCiConstants.ProtocolName,
            Context: HubAction[this.props.hubAction]
        });
    }

    public render(): JSX.Element {
        const textfieldIds = ["nuget-textfield-label1", "nuget-textfield-label2", "nuget-textfield-label3"];
        const viewOptions: IDropdownOption[] = [];
        const isUserPublicOrAnonymous = isUserAnonymousOrPublic(this.props.pageContext, this.props.feed);
        viewOptions.push({
            key: PackageResources.FeedViewDropdown_AllViews_Key,
            text: PackageResources.FeedViewDropdown_ConnectToFeed_AllViews_Name
        } as IDropdownOption);

        if (this.props.feedViews) {
            this.props.feedViews.forEach(view => {
                viewOptions.push({
                    key: view.id,
                    text: "@" + view.name
                } as IDropdownOption);
            });
        }

        return (
            <div className="nuget-connect-to-feed-panel connect-to-feed-panel">
                {this.state.endpointUrl ? (
                    <div>
                        <div className="connect-to-feed-section">
                            <div className="connect-to-feed-section-title" role="heading" aria-level={2}>
                                {PackageResources.NuGetConnectControl_VSHeadingText}
                            </div>
                            <div className="connect-to-feed-views-dropdown">
                                <LabelWithExternalLink
                                    pageContext={this.props.pageContext}
                                    labelText={PackageResources.FeedViewDropdown_ConnectToFeed_Label}
                                    linkText={PackageResources.FeedViewDropdown_ConnectToFeed_Label_Link}
                                    href={FwLinks.ConnectToFeedViewWhatsThis}
                                    ciContext={NuGetCiConstants.NuGetConnectToFeedPanel}
                                />
                                <Dropdown
                                    className="views-dropdown"
                                    ariaLabel={PackageResources.FeedViewDropdown_ConnectToFeed_Label}
                                    options={viewOptions}
                                    onChanged={option => this._onViewDropdownChanged(option)}
                                    defaultSelectedKey={
                                        this.state.selectedView
                                            ? this.state.selectedView.id
                                            : PackageResources.FeedViewDropdown_AllViews_Key
                                    }
                                />
                            </div>
                            <div className="connect-to-feed-textfield-container">
                                <div className="connect-to-feed-sub-heading" id={textfieldIds[0]}>
                                    {PackageResources.NuGetConnectControl_PackageSourceText}
                                </div>
                                <CopyableTextField
                                    pageContext={this.props.pageContext}
                                    text={this.state.endpointUrl}
                                    buttonAriaLabel={PackageResources.AriaLabel_CopyToClipBoard_Nuget_Source_Copy}
                                    textFieldAriaLabel={PackageResources.AriaLabel_CopyToClipBoard_Nuget_Source}
                                    telemetryProperties={
                                        {
                                            commandName: CiConstants.CopyCommandFeedURL,
                                            feedName: this.props.feed.name,
                                            protocol: NuGetCiConstants.ProtocolName
                                        } as ICopyableTextFieldTelemetry
                                    }
                                    ariaDescribedByIdForLabelText={textfieldIds[0]}
                                />
                            </div>
                            <ExternalLink
                                pageContext={this.props.pageContext}
                                className="connect-to-feed-link"
                                href={PackageResources.NuGetConnectControl_SetupVisualStudioLink}
                                ciContext={NuGetCiConstants.NuGetConnectToFeedPanel}
                            >
                                {PackageResources.NuGetConnectControl_SetupVisualStudioLinkText}
                            </ExternalLink>
                        </div>
                        <div className="connect-to-feed-section">
                            {!this.state.selectedView && !isUserPublicOrAnonymous && (
                                <div>
                                    <div className="connect-to-feed-section-title" role="heading" aria-level={2}>
                                        {PackageResources.NuGetConnectControl_NuGetExeHeadingText}
                                    </div>
                                    <div className="connect-to-feed-sub-heading">
                                        {PackageResources.NuGetConnectControl_GetToolsText}
                                    </div>
                                    <div className="connect-to-feed-text">
                                        <div>
                                            <FormatComponent
                                                format={PackageResources.NuGetConnectControl_NuGetDownloadFormat}
                                            >
                                                <ExternalLink
                                                    pageContext={this.props.pageContext}
                                                    href={PackageResources.NuGetConnectControl_NuGetDownloadLink}
                                                    ciContext={NuGetCiConstants.NuGetConnectToFeedPanel}
                                                >
                                                    {PackageResources.NuGetConnectControl_NuGetDownloadLinkText}
                                                </ExternalLink>
                                            </FormatComponent>
                                        </div>
                                        {this.isHosted && (
                                            <div>
                                                <FormatComponent
                                                    format={
                                                        PackageResources.NuGetConnectControl_CredentialProviderDownloadFormat
                                                    }
                                                >
                                                    <ExternalLink
                                                        pageContext={this.props.pageContext}
                                                        href={
                                                            PackageResources.NuGetConnectControl_CredentialProviderDownloadLink
                                                        }
                                                        ciContext={NuGetCiConstants.NuGetConnectToFeedPanel}
                                                    >
                                                        {
                                                            PackageResources.NuGetConnectControl_CredentialProviderDownloadLinkText
                                                        }
                                                    </ExternalLink>
                                                </FormatComponent>
                                            </div>
                                        )}
                                    </div>
                                    <div className="connect-to-feed-sub-heading" id={textfieldIds[1]}>
                                        {PackageResources.NuGetConnectControl_AddFeedText}
                                    </div>
                                    <CopyableTextField
                                        pageContext={this.props.pageContext}
                                        text={Utils_String.format(
                                            PackageResources.NuGetConnectControl_NuGetSourceCommand,
                                            this._getFullyQualifiedFeedName(),
                                            this.state.endpointUrl
                                        )}
                                        buttonAriaLabel={
                                            PackageResources.AriaLabel_CopyToClipBoard_Nuget_AddSource_Copy
                                        }
                                        textFieldAriaLabel={PackageResources.AriaLabel_CopyToClipBoard_Nuget_AddSource}
                                        telemetryProperties={
                                            {
                                                commandName: CiConstants.CopyCommandAddSource,
                                                feedName: this.props.feed.name,
                                                protocol: NuGetCiConstants.ProtocolName
                                            } as ICopyableTextFieldTelemetry
                                        }
                                        ariaDescribedByIdForLabelText={textfieldIds[1]}
                                    />
                                    <div className="connect-to-feed-sub-heading" id={textfieldIds[2]}>
                                        {PackageResources.NuGetConnectControl_PushPackageText}
                                    </div>
                                    <CopyableTextField
                                        pageContext={this.props.pageContext}
                                        text={Utils_String.format(
                                            PackageResources.NuGetConnectControl_NuGetPushCommand,
                                            this.props.feed.name
                                        )}
                                        buttonAriaLabel={PackageResources.AriaLabel_CopyToClipBoard_Nuget_Push_Copy}
                                        textFieldAriaLabel={PackageResources.AriaLabel_CopyToClipBoard_Nuget_Push}
                                        telemetryProperties={
                                            {
                                                commandName: CiConstants.CopyCommandPush,
                                                feedName: this.props.feed.name,
                                                protocol: NuGetCiConstants.ProtocolName
                                            } as ICopyableTextFieldTelemetry
                                        }
                                        ariaDescribedByIdForLabelText={textfieldIds[2]}
                                    />
                                </div>
                            )}
                            <ExternalLink
                                pageContext={this.props.pageContext}
                                className="connect-to-feed-link"
                                href={PackageResources.NuGetConnectControl_NuGet2xDocLink}
                                ciContext={NuGetCiConstants.NuGetConnectToFeedPanel}
                            >
                                {PackageResources.NuGetConnectControl_NuGet2xDocLinkText}
                            </ExternalLink>
                            <ExternalLink
                                pageContext={this.props.pageContext}
                                className="connect-to-feed-link"
                                href={PackageResources.NuGetConnectControl_AuthTroubleDocLink}
                                ciContext={NuGetCiConstants.NuGetConnectToFeedPanel}
                            >
                                {PackageResources.NuGetConnectControl_AuthTroubleDocLinkText}
                            </ExternalLink>
                        </div>
                    </div>
                ) : (
                    <PackageMessagePanel message={PackageResources.ConnectControl_EndpointNotAvailable} />
                )}
            </div>
        );
    }

    private _onViewDropdownChanged(option: IDropdownOption) {
        if (option.key === PackageResources.FeedViewDropdown_AllViews_Key) {
            const endpointUrl = this.NuGetRestApiUrlGeneratorService.getServiceIndexUrl(
                this.props.feed.name,
                this.showProjectInUrl
            );

            this.setState({
                endpointUrl,
                selectedView: null
            } as INuGetConnectToFeedPanelState);
        } else {
            const feedName = this.props.feed.name + option.text;
            const endpointUrl = this.NuGetRestApiUrlGeneratorService.getServiceIndexUrl(
                feedName,
                this.showProjectInUrl
            );
            this.setState({
                endpointUrl,
                selectedView: {
                    id: option.key,
                    name: option.text,
                    _links: null,
                    url: null,
                    type: null
                } as FeedView
            } as INuGetConnectToFeedPanelState);
        }
    }

    private get NuGetRestApiUrlGeneratorService(): INuGetRestApiUrlGeneratorService {
        return this.props.pageContext.getService<INuGetRestApiUrlGeneratorService>("INuGetRestApiUrlGeneratorService");
    }

    private _getFullyQualifiedFeedName(view: FeedView = null): string {
        let selectedView: FeedView = null;
        if (view != null) {
            selectedView = view;
        } else if (this.state && this.state.selectedView) {
            selectedView = this.state.selectedView;
        }

        if (selectedView != null) {
            return this.props.feed.name + (selectedView.name.indexOf("@") === -1 ? "@" : "") + selectedView.name;
        }

        return this.props.feed.name;
    }

    private get isHosted(): boolean {
        return this.props.pageContext.getService<IVssPageService>("IVssPageService").getData().isHosted;
    }
}
